import { Autocomplete, Box, Grid, IconButton, Stack, SxProps, TextField, Theme, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { debounce } from 'lodash';
import { AddressDto, AddressListResult } from 'src/models/address';
import { VigoTooltip } from '../vigo-tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import { VigoTextInput } from '../vigo-textinput';
import {
  useSearchResultsQuery,
  useSelectedAddressQuery,
  useSiteAddressQuery
} from 'src/queries/components/vigo-address';
import ClearIcon from '@mui/icons-material/Clear';
import { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';
import { useIsFetching } from '@tanstack/react-query';
import { useAppSelector } from 'src/store/baseHooks';

export interface VigoAddressProps {
  readonly?: boolean;
  width?: string;
  value?: AddressDto;
  onChange?: (data: AddressDto) => any;
  sx?: SxProps<Theme>;
  placeholder?: string;
  showPropertyName?: boolean;
  propertyNameLabel?: string;
  propertyNameRequired?: boolean;
  focusEnabled?: boolean;
  companyID?: string;
  errors?: Record<string, FieldError> | undefined;
  disableSiteAddresses?: boolean | undefined;
  required?: boolean;
  CustomNoOptionsText?: React.ReactNode;
  isDirty?: boolean;
  onClear?: () => void;
  disabled?: boolean;
}

const VigoAddress = React.forwardRef(
  (
    {
      readonly,
      width = '100%',
      placeholder = '',
      value,
      onChange,
      sx,
      showPropertyName,
      propertyNameLabel = '',
      propertyNameRequired = false,
      errors = undefined,
      focusEnabled = false,
      companyID,
      disableSiteAddresses = false,
      required = false,
      CustomNoOptionsText,
      isDirty = false,
      onClear,
      disabled = false
    }: VigoAddressProps,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ref
  ) => {
    const [autoCompleteValue, setAutoCompleteValue] = useState<AddressListResult | null>(null);
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState<AddressListResult[]>([]);
    const [componentAddress, setComponentAddress] = useState<AddressDto | null>(value ?? {});
    const [openAutoComplete, setOpenAutoComplete] = useState(false);
    const doSearch = useRef(false);
    const propertyNameRef = useRef<null | HTMLElement>(null);
    const autoCompleteRef = useRef<null | HTMLElement>(null);
    const searchAfterCharsEntered = 2;
    const [readOnly, setReadOnly] = useState(readonly);
    const [customerID, setCustomerID] = useState(companyID);

    const uiFlowMode = useAppSelector(state => state.uiflow.uiFlowMode);

    const siteAddressesQuery = useSiteAddressQuery(customerID ?? '', customerID != null);

    const isFetchingResults = useIsFetching(['addressSearchResults']);
    const isFetchingSelectedAddress = useIsFetching(['selectedAddress']);

    //Find
    const searchResults = useSearchResultsQuery(
      inputValue.trim(),
      customerID ?? '',
      '',
      data => {
        if (data) {
          let newList: AddressListResult[] = [];

          let hasValue = false;

          if (autoCompleteValue) {
            newList = [autoCompleteValue];
            hasValue = true;
          }
          newList = [...newList, ...data.savedAddresses!, ...data.matchedAddresses!];

          if (!disableSiteAddresses) {
            newList = [...data.siteAddresses!, ...newList];
          }

          setOptions(newList);

          if (newList.length == 2 && hasValue) {
            setAutoCompleteValue(GetAddressStringFromResult(newList[1]));
            setOpenAutoComplete(false);
          }

          if (newList.length == 1 && !hasValue) {
            setAutoCompleteValue(GetAddressStringFromResult(newList[0]));
            setOpenAutoComplete(false);
          }
        } else {
          setOptions([]);
        }
      },

      inputValue.trim().length > searchAfterCharsEntered && doSearch.current
    );

    const GetIDStringFromAddress = (data: AddressListResult) => {
      if (data) {
        if (data.cacheID != null && data.cacheID.length != 0) {
          return 'cacheID=' + data.cacheID.toUpperCase();
        }
        if (data.siteAddressID != null && data.siteAddressID.length != 0) {
          return 'siteAddressID=' + data.siteAddressID.toUpperCase();
        }
        if (data.lookUpID && data.lookUpID.length != 0) {
          return 'lookUpID=' + data.lookUpID.toUpperCase();
        }
        if (data.savedAddressID && data.savedAddressID.length != 0) {
          return 'savedAddressID=' + data.savedAddressID.toUpperCase();
        }
      }

      return '';
    };

    const GetIDFromIDString = (data: string) => {
      const result = data.substring(data.indexOf('=') + 1);

      return result;
    };

    //Retrieve
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const selectedAddress = useSelectedAddressQuery(
      GetIDStringFromAddress(autoCompleteValue!),
      '',
      (data: any) => {
        doSearch.current = false;
        if (data) {
          // Add a property to identify when the address is a saved address
          if (Object.hasOwn(autoCompleteValue ?? {}, 'savedAddressID')) {
            data.isSavedAddress = true;
          }

          setComponentAddress(data);
          onChange && onChange(data);

          setOptions([]);

          if (focusEnabled) {
            propertyNameRef.current && propertyNameRef.current.focus();
          }
        }
      },
      GetIDStringFromAddress(autoCompleteValue!) != '' &&
        (inputValue.trim().length > searchAfterCharsEntered || siteAddressesQuery.data != null)
    );

    //build the text to show, should match the text returned from the API
    const GetAddressString = (data: AddressDto): string => {
      const Address = data;

      return data === null
        ? ''
        : (Address.line1 ? Address.line1 + ', ' : '') +
            (Address.line2 ? Address.line2 + ', ' : '') +
            (Address.line3 ? Address.line3 + ', ' : '') +
            (Address.postalCode ? Address.postalCode : '');
    };

    //Remove line1 from the display
    const GetAddressStringFromResult = (data: AddressListResult): AddressListResult => {
      const result: AddressListResult = { ...data };

      if (data.line1 && result.text) {
        result.text = result.text.replace(data.line1 + ',', '')?.trim();
      }

      return result;
    };

    const debouncedSearch = useRef(
      debounce(async value => {
        if (doSearch.current) {
          setInputValue(value);
        }
      }, 200)
    ).current;

    /**
     * Find error for given field name and return the error message.
     *
     * @param fieldName keyof AddressDto
     * @returns string | undefined
     */
    const fieldError = (fieldName: keyof AddressDto): string | undefined => {
      if (!errors || !Object.keys(errors).length) return undefined;

      if (!errors.hasOwnProperty(fieldName)) return undefined;

      const fieldError: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined =
        errors[fieldName as keyof typeof errors as string];

      return (fieldError?.message as string) ?? '';
    };

    /**
     * Check if error object is present and contains specified error properties which
     * would indicate the address is missing and the autocomplete should be in error state.
     *
     * * Address is deemed missing if the 'postalCode' property is falsy
     *
     * @param errorObj
     * @returns boolean
     */
    const containsErrorForMissingAddress = (errorObj: Record<string, FieldError> | undefined): boolean => {
      if (!errorObj || Object.keys(errorObj).length === 0) return false;

      const { postalCode } = errorObj;

      return Boolean(postalCode);
    };

    /**
     * Clears the address by resetting the autocomplete value, component address, and calling the onChange function with an empty object. It also sets the readOnly flag to false.
     *
     * @return {void} This function does not return anything.
     */
    const clearAddress = (): void => {
      setAutoCompleteValue(null);
      setComponentAddress(null);
      onChange && onChange({});
      setReadOnly(false);

      if (onClear && typeof onClear === 'function') {
        onClear();
      }
    };

    function getReadOnly(): boolean {
      if (readonly) {
        return true;
      }

      if (isFetchingResults > 0 || isFetchingSelectedAddress > 0) {
        return true;
      }

      return false;
    }

    useEffect(() => {
      return () => {
        debouncedSearch.cancel();
      };
    }, [debouncedSearch]);

    useEffect(() => {
      if (inputValue.trim().length <= searchAfterCharsEntered) {
        setOptions([]);
      }
    }, [inputValue]);

    useEffect(() => {
      if (value) {
        const newValue: AddressListResult = {
          text: GetAddressString(value)
        };

        setAutoCompleteValue(newValue);
        setComponentAddress(value);
        setOptions([]);
        doSearch.current = false;
        setInputValue('');

        if (newValue.text !== '') {
          setReadOnly(true);
        }
      } else {
        clearAddress();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => {
      setCustomerID(companyID);

      // Reset the address if the companyID changes and the current address is a site address
      if (
        (uiFlowMode === 'add' && value?.isSiteAddress) ||
        (uiFlowMode === 'edit' && isDirty && value?.isSiteAddress)
      ) {
        clearAddress();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyID]);

    useEffect(() => {
      setReadOnly(readonly || false);
    }, [readonly]);

    return (
      <Stack direction="column" width={width} sx={{ ...sx }}>
        <Grid container rowSpacing={3.25} columnSpacing={1}>
          <Grid item xs={12} md={12}>
            <Autocomplete
              disabled={disabled}
              disableClearable
              ref={autoCompleteRef}
              open={openAutoComplete && options.length > 0}
              onOpen={() => {
                setOpenAutoComplete(true);
              }}
              onClose={() => {
                setOpenAutoComplete(false);
              }}
              readOnly={readOnly}
              id="address-searcher"
              sx={{ width: 1 }}
              getOptionLabel={option => option.text ?? ''}
              filterOptions={x => x}
              filterSelectedOptions
              options={options!}
              autoComplete={false}
              includeInputInList={false}
              value={autoCompleteValue as AddressListResult | undefined}
              noOptionsText={CustomNoOptionsText ?? 'No addresses found'}
              size="small"
              forcePopupIcon={false}
              loading={searchResults.isLoading && searchResults.fetchStatus !== 'idle'}
              onChange={(event, newValue) => {
                setOptions(newValue ? [newValue, ...options] : options);

                setAutoCompleteValue(newValue ? GetAddressStringFromResult(newValue) : {});
              }}
              onInputChange={(event, newInputValue) => {
                debouncedSearch(newInputValue);
              }}
              isOptionEqualToValue={(option: AddressListResult, value: AddressListResult) => {
                const optValue = GetIDStringFromAddress(option);
                const valValue = GetIDStringFromAddress(value);

                return optValue === valValue;
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  required={required}
                  label={autoCompleteValue?.text ? 'Address' : 'Search for an address'}
                  placeholder={placeholder}
                  onKeyDown={() => {
                    doSearch.current = true;
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchResults.isLoading && searchResults.fetchStatus !== 'idle' && (
                          <CircularProgress color="inherit" size={20} />
                        )}
                        {siteAddressesQuery.isLoading && siteAddressesQuery.fetchStatus !== 'idle' && (
                          <CircularProgress color="inherit" size={20} />
                        )}
                        {!readonly && !disableSiteAddresses && customerID && (
                          <VigoTooltip title="Site Addresses" enterDelay={500}>
                            <IconButton
                              tabIndex={-1}
                              sx={{ padding: '2px' }}
                              onClick={() => {
                                if (siteAddressesQuery.data) {
                                  setReadOnly(false);
                                  if (autoCompleteValue) {
                                    setOptions([autoCompleteValue, ...siteAddressesQuery.data]);
                                  } else {
                                    setOptions(siteAddressesQuery.data);
                                  }
                                  setOpenAutoComplete(true);
                                }
                              }}>
                              <WarehouseIcon />
                            </IconButton>
                          </VigoTooltip>
                        )}
                        {autoCompleteValue && !readonly && (
                          <VigoTooltip title="Clear" enterDelay={500}>
                            <IconButton
                              tabIndex={-1}
                              sx={{ padding: '2px' }}
                              onClick={() => {
                                clearAddress();
                              }}>
                              <ClearIcon />
                            </IconButton>
                          </VigoTooltip>
                        )}
                      </>
                    )
                  }}
                  error={containsErrorForMissingAddress(errors)}
                  helperText={containsErrorForMissingAddress(errors) ? 'Address is required' : ''}
                />
              )}
              renderOption={(props, option) => {
                const itemValue = GetIDStringFromAddress(option);
                const itemKey = GetIDFromIDString(itemValue);

                return (
                  <li {...props} key={itemKey} value={itemValue}>
                    <Grid container alignItems="center">
                      <Grid item>
                        {(itemValue.includes('cacheID') || itemValue.includes('lookUpID')) && (
                          <VigoTooltip title="Matched Address">
                            <Box component={LocationOnIcon} sx={{ color: 'text.secondary', mr: 2 }} />
                          </VigoTooltip>
                        )}
                        {itemValue.includes('siteAddress') && (
                          <VigoTooltip title="Site Address">
                            <Box component={WarehouseIcon} sx={{ color: 'text.secondary', mr: 2 }} />
                          </VigoTooltip>
                        )}
                        {itemValue.includes('savedAddress') && (
                          <VigoTooltip title="Saved Address">
                            <Box component={HomeWorkIcon} sx={{ color: 'text.secondary', mr: 2 }} />
                          </VigoTooltip>
                        )}
                      </Grid>
                      <Grid item xs>
                        <Typography>
                          {option.text}
                          {option.addressType == 'R' && ' - Residential'}
                          {option.addressType == 'N' && ' - Non Residential'}
                          {option.addressType == 'U' && ''}
                        </Typography>
                      </Grid>
                    </Grid>
                  </li>
                );
              }}
            />
          </Grid>
          {showPropertyName && (
            <Grid item xs={12} md={12}>
              <VigoTextInput
                fullWidth
                ref={propertyNameRef}
                name="Property"
                label={propertyNameLabel}
                value={componentAddress?.buildingName ?? ''}
                onChange={e => {
                  setComponentAddress({ ...componentAddress, buildingName: e.target.value });
                  onChange && onChange({ ...componentAddress, buildingName: e.target.value });
                }}
                inputProps={{ readOnly: getReadOnly() }}
                error={!!fieldError('buildingName')}
                errormessage={fieldError('buildingName')}
                {...(propertyNameRequired && { required: true })}
              />
            </Grid>
          )}
        </Grid>
      </Stack>
    );
  }
);

export default VigoAddress;
